import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import translate from "@insite/client-framework/Translate";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, StyledTypography } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductAvailability, { ProductAvailabilityStyles } from "@MasterBluePrint/Overrides/Components/ProductAvailability";
import ProductContextAvailability from "@MasterBluePrint/Overrides/Components/ProductContextAvailability";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
});

type Props = ReturnType<typeof mapStateToProps> & HasProductContext;

export interface RfqQuoteDetailsRequestedDetailsGridStyles {
    mainWrapper?: InjectableCss;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    quoteLineInfoGridItem?: GridItemProps;
    quoteLineInfoContainer?: GridContainerProps;
    infoLeftColumn?: GridItemProps;
    infoLeftColumnContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndSubtotalGridItem?: GridItemProps;
    quantityAndSubtotalContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    subtotalGridItem?: GridItemProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    totalWithoutVatText?: TypographyPresentationProps;
    extendedStyles?: ProductAvailabilityStyles;

    gridContainer?: GridContainerProps;
    quoteLineGridHeadWrapper?: GridItemProps;
    quoteLineGridHead?: InjectableCss;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productInfoPartNumbersGridItem?:GridItemProps;	
	productInfoPartNumbersGridContainer?:GridContainerProps;
    productInfoErpNumberGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoManufacturerItemGridItem?: GridItemProps;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoCustomerProductGridItem?: GridItemProps;
    customerProductNameHeadingAndText?: SmallHeadingAndTextStyles;
    quoteLinePriceInfoGridItem?: GridItemProps;
    quoteLineInfoGridContainer?: GridContainerProps;
    quoteLinePriceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productInfoTotalsGridItem?: GridItemProps;
    productInfoTotalsGridContainer?: GridContainerProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoAvailabilityGridItem?: GridItemProps;
    productInfoQtyOrderedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoAvailHeadingAndText?: InjectableCss;
    productInfoAvailabilityHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    productImageLink?: LinkPresentationProps;
    productItemImage?: LazyImageProps;
}

export const rfqQuoteDetailsRequestedDetailsGridStyles: RfqQuoteDetailsRequestedDetailsGridStyles = {
    productImageLink: {
        css: css`
            img {
                border: 1px solid ${themeColors.colorBrightGray};
                border-radius: 2px;
            }
        `,
    },
    productItemImage:{
        css: css`
            width: 5.6575rem;
            border: 1px solid ${themeColors.colorBrightGray};
            border-radius: 2px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 10.25rem;
                            @media print{
                                width: 3.2rem;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },    
    gridContainer: {
        gap: 10,
    },
    quoteLineGridHeadWrapper: {
        width: 12,
        css: css`
            padding: 0.625rem 0.9375rem 0.625rem 0;
            margin: 0 0.625rem 0 0.625rem !important;
            border-top: 1px solid ${themeColors.colorBorderBrands};
            border-bottom: 1px solid ${themeColors.colorBorderBrands};

            .total-items {
                min-width: 18.75rem;
            }
        `,
    },
    quoteLineGridHead: {
        css: css`
            display: block;
            width: 100%;

            h3 {
                color: ${themeColors.colorActiveMenuLink};
                font-family: 'Lato',sans-serif;
                font-size: ${themeFontSizes.size16};
                font-weight: bold;
                letter-spacing: 0.5px;
                line-height: 24px;
            }

            .total-items {
                h3 {
                    color: ${themeColors.colorPrimary};
                    font-family: 'Roboto', sans-serif;
                    font-size: ${themeFontSizes.size16};
                    font-style: italic;
                    font-weight: ${themeFontWeights.fontWeightMedium};
                    letter-spacing: 0;
                }
            }

            > div {
                display: inline-block;
                text-align: left;
            }

            .sub-total {
                display: inline-block;
                text-align: right;
                float: right;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            h3, .price, .qty {
                                display: none;
                            }
                            .total-items {
                                h3 {
                                    display: block;
                                }
                            }
                            > div {
                                display: block;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },

    productInfoBrandDescriptionGridItem: {
        width: 12,
        css: css`

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 0 !important;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    productInfoBrandDescriptionGridContainer: {
        gap: 5,
    },
    productInfoBrandGridItem: {
        width: 12,
        css: css`
            span {
                color: ${themeColors.colorCharcoalGrey};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size14};
                letter-spacing: 0;
                line-height: 24px;
                text-transform: uppercase;
            }
        `,
    },
    productInfoDescriptionGridItem: {
        width: 12,
        css: css`
            a[data-test-selector="productDescriptionLink"],
            a[data-test-selector="productDescriptionLink"] > span {
                color: ${themeColors.colorPrimary};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size16};
                font-weight: ${themeFontWeights.fontWeightMedium};
                letter-spacing: 0;
                line-height: 24px;
            }
        `,
    },
    productInfoPartNumbersGridItem: {
        width: 12,
        css: css`        
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin-top: 1.25rem !important;
                    `,
                ],
                "min",
            )}
        `,
    },
    productInfoPartNumbersGridContainer: {
        css: css`
            p[data-test-selector="heading"] {
                color: ${themeColors.colorPrimary};
                font-family: 'Lato',sans-serif;
                font-size: ${themeFontSizes.size16};
                letter-spacing: 0;
                line-height: 20px;
                white-space: nowrap;
                text-transform: none;
                margin-right: 0.3125rem;

                &:after {
                    content:":";
                    display:inline-block;
                }
            }

            p[data-test-selector="text"] {
                color: ${themeColors.colorCharcoalGrey};
                font-family: 'Lato',sans-serif;
                font-size: ${themeFontSizes.size16};
                letter-spacing: 0;
                line-height: 20px;
            }

            .product-info-seperator {
                padding-bottom: 0.3125rem;

                &:last-child {
                    padding-bottom: 0;
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding-bottom: 0.75rem;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )
                }
            }
        `,
    },
    productInfoErpNumberGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 0;

            > div {
                flex-direction: row;

                > p[data-test-selector="heading"] {
                    &:after {
                        content: none !important;
                    }
                }
            }
        `,
    },
    productInfoManufacturerItemGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
            > div {
                flex-direction: row;
            } 
        `,
    },
    productInfoCustomerProductGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
            > div {
                flex-direction: row;

                > p[data-test-selector="heading"] {
                    &:after {
                        content: none !important;
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            > div {
                                flex-direction: column;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    quoteLinePriceInfoGridItem: {
        width: [12, 12, 8, 7, 8],
        printWidth: 6,
    },
    quoteLineInfoGridContainer: {
        gap: 5,
        css: css`
            @media print{
                min-width: 20.625rem;
            }
            @media print and (orientation: landscape) {
                min-width: 34.375rem;
            }
            > div {
                width: calc(100% + 1px) !important;

            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        padding-right: 0.5625rem;
                    `,
                ],
                "min",
            )}
        `,
    },
    quoteLinePriceGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            color: ${themeColors.colorCharcoalGrey};
            font-family: 'Lato', sans-serif;
            font-size: ${themeFontSizes.size16};
            letter-spacing: 0.5px;
            line-height: 24px;
            
            p[data-test-selector="heading"] {
                color: ${themeColors.colorInfo};
                font-weight: bold;
                white-space: nowrap;
                margin-right: 0.3125rem;

                &:after {
                    content:":";
                    display:inline-block;
                }
                
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        null,
                        css`
                            min-width: 33%;
                        `,
                    ],
                    "minMax",
                )}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            p[data-test-selector="heading"] {
                                display: none;
                            }
                        `,
                    ],
                    "min",
                )}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-top: 0.375rem;
                            padding-bottom: 0.625rem;
                            border-bottom: 1px solid #E1E1E1;
                            @media print{
                                border:none;
                            }
                        > div {
                            flex-direction: row;
                            
                            > p[data-test-selector="heading"] {
                                font-size: ${themeFontSizes.size16};
                                color: ${themeColors.colorEndeavour};
                                font-weight: bold;
                                text-transform: capitalize;
                            }
                        }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    secondaryVatLabelText: {
        size: 12,
        css: css`
            color: ${themeColors.colorCharcoalGrey};
            font-family: 'Lato', sans-serif;
            font-size: ${themeFontSizes.size16};
            letter-spacing: 0.5px;
            line-height: 24px;
        `,
    },
    productInfoTotalsGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            flex-direction: column;
            color: ${themeColors.colorCharcoalGrey};
            font-family: 'Lato', sans-serif;
            font-size: ${themeFontSizes.size16};
            letter-spacing: 0.5px;
            line-height: 24px;
            
            p[data-test-selector="heading"] {
                color: ${themeColors.colorEndeavour};
                font-weight: bold;
                white-space: nowrap;
                margin-right: 0.3125rem;

                &:after {
                    content:":";
                    display:inline-block;
                }
                
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        null,
                        css`
                            min-width: 33%;
                        `,
                    ],
                    "minMax",
                )}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            p[data-test-selector="heading"] {
                                display: none;
                            }
                        `,
                    ],
                    "min",
                )}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-top: 0.625rem;

                            @media print{
                                border: none;
                            }
                        > div {
                            flex-direction: row;
                            
                            > p[data-test-selector="heading"] {
                                font-size: ${themeFontSizes.size16};
                                color: ${themeColors.colorEndeavour};
                                font-weight: ${themeFontWeights.fontWeightForBold};
                                text-transform: capitalize;
                            }
                        }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    productInfoTotalsGridContainer: {
        gap: 10,
    },
    productInfoQtyOrderedGridItem: {
        width: [12, 12, 2, 2, 2],
        css: css`
            color: ${themeColors.colorCharcoalGrey};
            font-family: 'Lato', sans-serif;
            font-size: ${themeFontSizes.size16};
            letter-spacing: 0.5px;
            line-height: 24px;

            p[data-test-selector="heading"] {
                color: ${themeColors.colorEndeavour};
                font-weight: bold;
                white-space: nowrap;
                margin-right: 0.3125rem;

                &:after {
                    content:":";
                    display:inline-block;
                }
            }
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css` 
                            p[data-test-selector="heading"] {
                                display: none;
                            }
                            p[data-test-selector="text"] {
                                padding-left: 0;
                            }
                        `,
                        null,
                        null,
                    ],
                    "min",
                )
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`                        
                            border-bottom: 1px solid #E1E1E1;
                            padding-top: 0.375rem;
                            padding-bottom: 0.625rem;
                            
                            @media print{
                                border:none;
                            }
                            > div {
                                flex-direction: row;
                                
                                > p[data-test-selector="heading"] {
                                    font-size: ${themeFontSizes.size16};
                                    color: ${themeColors.colorEndeavour};
                                    font-weight: bold;
                                    text-transform: capitalize;
                                }
                            }
                            p[data-test-selector="text"] {
                                padding-left: 0.3125rem;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    productInfoSubtotalGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            align-items: flex-end;
            align-content: flex-end;
            color: ${themeColors.colorCharcoalGrey};
            font-family: 'Lato', sans-serif;
            font-size: ${themeFontSizes.size16};
            font-weight: ${themeFontWeights.fontWeightForBold};
            letter-spacing: 0.5px;
            line-height: 24px;
            text-align: right;

            p[data-test-selector="heading"] {
                color: ${themeColors.colorEndeavour};
                font-weight: ${themeFontWeights.fontWeightForBold};
                white-space: nowrap;
                margin-right: 0.3125rem;

                &:after {
                    content:":";
                    display:inline-block;
                }
            }

            > div {
                align-items: flex-end;
                align-content: flex-end;
                text-align: right;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            p[data-test-selector="heading"] {
                                display: none;
                            }

                            p[data-test-selector="text"] {
                                color: ${themeColors.colorCharcoalGrey};
                                font-weight:  ${themeFontWeights.fontWeightForBold};
                                font-size: ${themeFontSizes.size16};
                            }
                        `,
                        null,
                        null,
                    ],
                    "min",
                )
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`                        
                            padding-top: 0.5rem;
                            padding-bottom: 0.625rem;
                            > div {
                                flex-direction: row;
                                align-items: flex-start;
                                align-content: flex-start;
                                text-align: left;

                                p[data-test-selector="heading"] {
                                    font-size: ${themeFontSizes.size16};
                                    color: ${themeColors.colorEndeavour};
                                    font-weight:  ${themeFontWeights.fontWeightForBold};
                                    text-transform: capitalize;
                                }
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },

    productInfoAvailabilityGridItem:{
        width: [12, 12, 6, 6, 6],
        css: css`
            color: ${themeColors.colorCharcoalGrey};
            font-family: 'Lato', sans-serif;
            font-size: ${themeFontSizes.size16};
            letter-spacing: 0.5px;
            line-height: 24px;
            flex-direction: column;

            p[data-test-selector="heading"] {
                color: ${themeColors.colorEndeavour};
                font-weight: bold;
                white-space: nowrap;
                margin-right: 0.3125rem;

                &:after {
                    content:":";
                    display:inline-block;
                }
            }
            
            .adjust-spacing {
                div[class*="InventoryMessage"] {
                    margin-top: 0;
                }
            }

            &.quote-availability-info {
                div[class*="InventoryMessage"] > span.stock-container {
                    font-size: 1rem !important;
                    color: ${themeColors.colorPrimary};
                    padding-bottom: 0.375rem;

                    > span[class*="TypographyStyle"],
                    span[class^="TypographyStyle"] {
                        color: ${themeColors.colorPrimary};
                        font-weight: ${themeFontWeights.fontWeightForBold};

                        > span:first-child {
                            margin-right: 0.3125rem;
                        }

                        > span:last-child {
                            color: ${themeColors.colorCharcoalGrey};
                            font-weight: normal;
                        }
                    }
                }

                div:not(.availability-data) {
                    color: ${themeColors.colorPrimary};
                    font-weight: ${themeFontWeights.fontWeightForBold};

                    span[class*="TypographyStyle"]:not(.stock-container),
                    span[class^="TypographyStyle"]:not(.stock-container){
                        &:first-child {
                            margin-right: 0.3125rem;
                        }
                        &:last-child {
                            color: ${themeColors.colorCharcoalGrey};
                            font-weight: ${themeFontWeights.fontWeightNormal};                      
                        }
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            p[data-test-selector="heading"] {
                                display: none;
                            }
                            p[data-test-selector="text"] {
                                padding-left: 0;
                            }
                        `,
                        null,
                        null,
                    ],
                    "min",
                )
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`                        
                            border-bottom: 1px solid ${themeColors.colorLavenderPinocchio};
                            padding-top: 0.625rem;
                            padding-bottom: 0.625rem;                            
                            
                            .availability-data {
                                padding: 0.625rem 0 0.625rem 0.9375rem;
                            }
                            @media print{
                                border:none;
                            }
                            > div {
                                
                                > p[data-test-selector="heading"] {
                                    font-size: ${themeFontSizes.size16};
                                    color: ${themeColors.colorEndeavour};
                                    font-weight: ${themeFontWeights.fontWeightForBold};
                                    text-transform: capitalize;
                                }
                            }
                            p[data-test-selector="text"] {
                                padding-left: 0.3125rem;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    container: {
        css: css`
            margin: 0 0.625rem;
            border-bottom: 1px solid ${themeColors.colorLightPureBlue};
            padding: 1rem 0;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            border-top: 3px solid ${themeColors.colorLightPureBlue};
                            border-bottom: 3px solid ${themeColors.colorLightPureBlue};
                        `,
                    ],
                    "max",
                )}

        `,
    },
    productImageGridItem: {
        css: css`
            flex-basis: auto;
            max-width: 7.5rem;
            padding-left: 0.9375rem;
            @media print {
                //padding: 0;
            }
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-width: 10.25rem;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            min-width: 7.5rem;
                        `,
                    ],
                    "min",
                )}
            
        `,
    },
    quoteLineInfoGridItem: {
        width: 12,
        css: css`
            flex-basis: auto !important;
            padding-right: 0;

            &.mobile-content {
                display: none;
            }
            @media print {
                &.mobile-content {
                    display: none !important;
                }
            }            
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &.desktop-content {
                                padding-left: 0;
                                flex-basis: calc(100% - 130px);
                                max-width: calc(100% - 130px);
                                min-width: calc(100% - 130px);
                                .quoteLineOtherDetail {
                                    > div {
                                        .quote-product-info {
                                            min-width: 33%;
                                            @media print {
                                                min-width: 33%;
                                            }
                                        }
                                    }
                                }
                            }
                        `,
                    ],
                    "min",
            )}
            @media screen and (min-width:1300px) {
                &.desktop-content {
                    flex-basis: calc(100% - 130px);
                    max-width: calc(100% - 130px);
                    min-width: calc(100% - 130px);
                    .quoteLineOtherDetail {
                        > div {
                            .quote-product-info {
                                min-width: 33%;
                            }
                        }
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css` 
                            max-width: 100% !important;
                            padding-bottom: 0;

                            &.desktop-content {
                                padding-top: 0;
                                .product-description {
                                    padding-bottom: 10px;
                                    border-bottom: 1px solid #E1E1E1;

                                    @media print {
                                        display: none;
                                    }
                                }
                                .product-info {
                                    padding: 1.875rem 0 1.875rem 0.9375rem;
                                }
                                .quoteLineOtherDetail {
                                    > div {
                                        .quote-product-info {                                           
                                            border-bottom: 1px solid #E1E1E1;
                                            padding: 0 0.3125rem 0.3125rem;
                                            @media print {
                                                border:none;
                                            }
                                        }
                                    }
                                }
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }  
        `,
    },
    quoteLineInfoContainer: {
        gap: 10,
        css: css`
            margin-bottom: 15px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            > div {
                                width: calc(100% + 10px) !important;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    infoLeftColumn: {
        width: [12, 12, 4, 5, 4],
        printWidth: 6,
    },
    infoLeftColumnContainer: {
        gap: 5,
        css: css`
            margin-bottom: 0.9375rem;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 0;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },
    productBrandAndDescriptionGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    costCodeGridItem: { width: 12 },
    costCodeLabelText: {
        weight: "bold",
        css: css`
            margin-right: 10px;
        `,
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    totalWithoutVatText: {
        size: 20,
        css: css`
            margin-top: 5px;
        `,
    },
};

const styles = rfqQuoteDetailsRequestedDetailsGridStyles;

const RfqQuoteDetailsRequestedDetailsGrid = ({ quoteState, enableVat, vatPriceDisplay,productContext }: Props) => {
    const quote = quoteState.value;
    const { product } = productContext;
    if (!quote || !quote.quoteLineCollection) {
        return null;
    }

    const quotesCount = quote?.quoteLineCollection.length;
    const quotesLabel = quotesCount > 1 ? "Items in this Quote" : "Item in this Quote";
    
    function setWidthFromOffset() {
        setTimeout(() => {
            const elementPriceContainer = document.getElementById("priceContainer");
            const elementTotalItems = document.getElementById("totalItems");
            const elementPriceItem = document.getElementById("priceItem");
            const elementQtyItem = document.getElementById("quote-qty");
            const elementAvailItem = document.getElementById("quote-availability");
            const elementItemPriceHeading = document.getElementById("itemPrice");
            const elementItemQtyHeading = document.getElementById("itemQty");
            const elementItemAvailHeading = document.getElementById("itemAvail");
            const elementOrderLineCard = document.querySelectorAll('.quote-line-wrapper')[0];

            if (elementPriceContainer && elementTotalItems && elementPriceItem && elementItemPriceHeading && elementOrderLineCard && elementQtyItem && elementItemQtyHeading && elementAvailItem && elementItemAvailHeading ) {
                const leftOffsetNew = elementPriceContainer.getBoundingClientRect().left - elementOrderLineCard.getBoundingClientRect().left;
                const priceEleWidth = elementPriceItem.offsetWidth;
                const qtyEleWidth = elementQtyItem.offsetWidth;
                 const availEleWidth = elementAvailItem.offsetWidth;
                elementTotalItems.style.width = `${leftOffsetNew + 5}px`;
                elementItemPriceHeading.style.width = `${priceEleWidth}px`;
                elementItemQtyHeading.style.width = `${qtyEleWidth}px`;
                 elementItemAvailHeading.style.width = `${availEleWidth}px`;
            }
        }, 500);
    }

    if (document.body.clientWidth > 767) {
        setWidthFromOffset();
    }

    window.addEventListener("resize", setWidthFromOffset);
    
    return (
        <StyledWrapper {...styles.mainWrapper}>
            <GridContainer  {...styles.gridContainer}>
                <GridItem  {...styles.quoteLineGridHeadWrapper}>
                    <StyledWrapper {...styles.quoteLineGridHead}>
                        <Typography id="totalItems" as="div" className="total-items"><Typography as="h3">{quotesCount} {translate(quotesLabel)}</Typography></Typography>
                        <Typography id="itemPrice" as="div" className="price"><Typography as="h3">{translate("Price")}</Typography></Typography>
                        <Typography id="itemQty" as="div" className="qty"><Typography as="h3">{translate("QTY")}</Typography></Typography>
                        <Typography id="itemAvail" as="div" className="avaiability"><Typography as="h3">{translate("Availability")}</Typography></Typography>
                        <Typography as="div" className="sub-total"><Typography as="h3">{translate("SubTotal")}</Typography></Typography>
                    </StyledWrapper>
                </GridItem>
            
                {quote.quoteLineCollection.map(quoteLine => ( 
                    <GridContainer {...styles.container} key={`${quoteLine.productId}_${quoteLine.unitOfMeasure}`}  
                    data-test-selector={`quoteLine${quoteLine.productId}`}
                    className="quote-line-wrapper">
                        <GridItem {...styles.productImageGridItem} className="quote-product-img">
                            {/* <ProductImage product={quoteLine} extendedStyles={styles.productImage}/> */}
                             <Link {...styles.productImageLink} href={quoteLine.productUri}>
                                <LazyImage {...styles.productItemImage} src={quoteLine.smallImagePath} />
                            </Link>
                        </GridItem>
                        <GridItem {...styles.quoteLineInfoGridItem} data-test-selector="quoteProductInfoGrid" className="desktop-content">
                            <GridContainer {...styles.quoteLineInfoContainer} className="quoteLineOtherDetail">
                                <GridItem {...styles.infoLeftColumn} className="quote-product-info">
                                    <GridContainer {...styles.infoLeftColumnContainer}>

                                        <GridItem {...styles.productInfoBrandDescriptionGridItem} className="product-description">
                                            <GridContainer {...styles.productInfoBrandDescriptionGridContainer}>
                                                {quoteLine.brand && (
                                                    <GridItem {...styles.productInfoBrandGridItem}>
                                                        <ProductBrand brand={quoteLine.brand} extendedStyles={styles.productBrandStyles} />
                                                    </GridItem>
                                                )}
                                                <GridItem {...styles.productInfoDescriptionGridItem}>
                                                    <ProductDescription product={quoteLine} extendedStyles={styles.productDescriptionStyles} />
                                                </GridItem>
                                            </GridContainer>
                                        </GridItem>

                                        <GridItem {...styles.productInfoPartNumbersGridItem} className="product-info">
                                            <GridContainer {...styles.productInfoPartNumbersGridContainer}>
                                                <GridItem {...styles.productInfoErpNumberGridItem} className="product-info-seperator">
                                                    <SmallHeadingAndText
                                                        {...styles.erpNumberHeadingAndText}
                                                        heading={translate("Part #")}
                                                        text={quoteLine.erpNumber}                                                        
                                                    />
                                                </GridItem>
                                                {quoteLine.manufacturerItem && (
                                                    <GridItem {...styles.productInfoManufacturerItemGridItem} className="product-info-seperator">
                                                        <SmallHeadingAndText
                                                            {...styles.manufacturerHeadingAndText}
                                                            heading={translate("MFG #")}
                                                            text={quoteLine.manufacturerItem}
                                                        />
                                                    </GridItem>
                                                )}
                                                {quoteLine.customerName && (
                                                    <GridItem {...styles.productInfoCustomerProductGridItem} className="product-info-seperator">
                                                        <SmallHeadingAndText
                                                            {...styles.customerProductNameHeadingAndText}
                                                            heading={translate("My Part #")}
                                                            text={quoteLine.customerName}
                                                        />
                                                    </GridItem>
                                                )}
                                            </GridContainer>
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>

                                <GridItem {...styles.quoteLinePriceInfoGridItem} id="priceContainer">
                                    <GridContainer {...styles.quoteLineInfoGridContainer}>
                                        <GridItem {...styles.quoteLinePriceGridItem} id="priceItem">
                                            {quoteLine.pricing && (
                                                <>
                                                    <SmallHeadingAndText
                                                        {...styles.priceHeadingAndText}
                                                        heading={translate("Price")}
                                                        text={
                                                            (enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                                ? quoteLine.pricing.unitRegularPriceWithVatDisplay
                                                                : quoteLine.pricing.unitNetPriceDisplay) +
                                                            (quoteLine.unitOfMeasure ? ` / ${quoteLine.unitOfMeasure}` : "")
                                                        }
                                                    />
                                                    {enableVat && (
                                                        <>
                                                            <Typography as="p" {...styles.vatLabelText}>
                                                                {vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                                    ? `${translate("Inc. VAT")} (${quoteLine.pricing.vatRate}%)`
                                                                    : translate("Ex. VAT")}
                                                            </Typography>
                                                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                                <>
                                                                    <Typography {...styles.secondaryPriceText} as="p">
                                                                        {quoteLine.pricing.unitNetPriceDisplay +
                                                                            (quoteLine.unitOfMeasure ? ` / ${quoteLine.unitOfMeasure}` : "")}
                                                                    </Typography>
                                                                    <Typography as="p" {...styles.secondaryVatLabelText}>
                                                                        {translate("Ex. VAT")}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </GridItem>

                                        <GridItem {...styles.productInfoTotalsGridItem} className="qty-inner-box">
                                            <GridContainer {...styles.productInfoTotalsGridContainer}>
                                                <GridItem {...styles.productInfoQtyOrderedGridItem} id="quote-qty" className="quote-qty-val">
                                                    <SmallHeadingAndText
                                                        {...styles.productInfoQtyOrderedHeadingAndText}
                                                        heading={translate("QTY")}
                                                        text={`${quoteLine.qtyOrdered}`}
                                                    />
                                                </GridItem>

                                                <GridItem {...styles.productInfoAvailabilityGridItem} className="quote-availability-info" id="quote-availability">                                                    
                                                    <Typography as="p" {...styles.productInfoAvailHeadingAndText} data-test-selector="heading" className="quote-avail-val">
                                                        {translate("Availability")}
                                                    </Typography>
                                                    <Typography as="div" className="availability-data">
                                                        <ProductAvailability
                                                            productId={quoteLine.productId!}
                                                            trackInventory={quoteLine.trackInventory}
                                                            availability={quoteLine.availability!}
                                                            unitOfMeasure={quoteLine.unitOfMeasure}
                                                            isProductDetailsPage={false}                                   
                                                            extendedStyles={styles.extendedStyles}
                                                            qtyOnHand={quoteLine.qtyOnHand ?? 0}
                                                            isDirectShip={quoteLine.properties?.isDirectShip ?? false}
                                                            manufacturerItem={quoteLine?.manufacturerItem ?? false}
                                                            manufacturerName={quoteLine.properties?.manufacturer ?? false}
                                                            isDirectShipFromManufacturer={quoteLine.properties?.isDirectShipFromManufacturer ?? false}
                                                            inventoryOutofStock={quoteLine.properties?.inventoryOutofStock ?? ""}
                                                            brand={quoteLine.brand?.name}
                                                            sku={quoteLine.erpNumber}
                                                        /> 
                                                    </Typography>                                                    
                                                </GridItem>

                                                <GridItem {...styles.productInfoSubtotalGridItem} className="quote-subtotal-info">
                                                {quoteLine.pricing && (
                                                    <>
                                                    {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                        <SmallHeadingAndText
                                                            heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                                            text={quoteLine.pricing.extendedUnitNetPriceDisplay}
                                                            extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                                                        />
                                                    )}
                                                    <SmallHeadingAndText
                                                        heading={
                                                            !enableVat
                                                                ? translate("Subtotal")
                                                                : vatPriceDisplay !== "DisplayWithoutVat"
                                                                    ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                                                    : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                                        }
                                                        text={
                                                            enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                                ? quoteLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                                : quoteLine.pricing.extendedUnitNetPriceDisplay
                                                        }
                                                        extendedStyles={styles.productInfoSubtotalHeadingAndText}
                                                    />
                                                </>
                                                )}
                                                </GridItem>
                                            </GridContainer>
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                                
                                {quote.showCostCode && quoteLine.costCode && (
                                    <GridItem {...styles.costCodeGridItem}>
                                        <Typography {...styles.costCodeValueText} as="p">
                                            <Typography {...styles.costCodeLabelText}>{quote.costCodeLabel}:</Typography>
                                            {quoteLine.costCode}                                    
                                        </Typography>                                    
                                    </GridItem>
                                )}                                                        
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                ))}
            </GridContainer>            
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(RfqQuoteDetailsRequestedDetailsGrid));
